import { useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { DownloadSimple, UploadSimple } from "@phosphor-icons/react";

// Wide format: one row per package; reserved columns first, then one column per item label.
// Column-name aliases are accepted by the importer so operators can paste rows directly
// from their existing spreadsheets without remapping:
//   - packagename → name
//   - PACKAGE_HSNCode / package_hsn → ex_showroom_hsn
//   - HELMET_HSN → helmet_hsn  ·  ACCESORIES_HSN (typo) / accessories_hsn → accessories_hsn
//   - package_igst_pct / package_cgst_pct → igst_pct / cgst_pct
//   - companydiscount → company_discount
//   - model / category / type / vehicle_type → model_category  (SCOOTER / MOPED /
//     MOTORCYCLE / PREMIUM / SUPER PREMIUM / ELECTRIC / COMMERCIAL / OTHER — stored upper-case)
// Item cols: append "(OPTIONAL)" to a header to mark the item toggleable at sale time.
// IGST/CGST tax line items are auto-injected from igst_pct/cgst_pct — don't include them as columns.
// Helmet/Accessories prices are GST-INCLUSIVE (sticker prices).
// branch_type_code REQUIRED for multi-branch-type dealers (defaults to GENERAL if
// omitted). Use `AD` for sub-dealer / authorised-dealer packages, `SERVICE` for
// service-only packages. Same package name may exist once per branch type.
// active accepts true/false/yes/no/1/0/✔/✓.
// Profit-Per-Vehicle margins (Feb-2026): the last 4 columns are ₹ amounts used
// exclusively by the Profit-Per-Vehicle report. Snapshotted onto each sale at
// creation time. `discount_adj` is NOT a package field — it is computed
// per-sale (excess payment received vs written-off dues) at report time.
// `insurance_paid` comes from the Registration's insurance-stage amount and
// `insurance_charged` comes from the `Insurance` line-item's price — both
// derived automatically, not stored on the package.
const TEMPLATE_HEADER =
  "packagename,branch_type_code,model_category,description," +
  "Ex-showroom,Handling Charges,Life Tax,TR Charges,Accessories," +
  "Insurance(OPTIONAL),AMC(OPTIONAL),Additional Accessories(OPTIONAL),Helmet(OPTIONAL),Extended Warranty(OPTIONAL)," +
  "CompanyDiscount,PACKAGE_HSNCode,HELMET_HSN,ACCESORIES_HSN," +
  "package_igst_pct,package_cgst_pct," +
  "helmet_igst_pct,helmet_cgst_pct," +
  "accessories_igst_pct,accessories_cgst_pct," +
  "dealer_margin,mandatory_margin,helmet_margin,add_acc_margin," +
  "active\n" +
  "JUPITER 125 BSVI-DISC,GENERAL,SCOOTER,JUPITER 125 BSVI-DISC," +
  "73686.44,1180,11470,935,975," +
  "7097,999,3200,929,999," +
  "0,87112019,87116020,87116020," +
  "9,9," +
  "9,9," +
  "9,9," +
  "5000,1200,300,800," +
  "true\n" +
  "JUPITER 125 BSVI-DISC,AD,SCOOTER,JUPITER 125 BSVI-DISC (sub-dealer variant)," +
  "68000,0,11470,935,0," +
  ",,,,," +
  "0,87112019,87116020,87116020," +
  "9,9," +
  "9,9," +
  "9,9," +
  "3000,500,0,0," +
  "true\n";

function downloadBlob(content, filename) {
  const blob = new Blob([content], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function reportImportError(err) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object" && detail.duplicates) {
    const heads = detail.duplicates.slice(0, 4).join(", ");
    const more = detail.duplicates.length > 4 ? ` +${detail.duplicates.length - 4} more` : "";
    toast.error(`Duplicate names — import aborted: ${heads}${more}`);
  } else {
    toast.error(formatApiError(err));
  }
}

function usePackageCsv(onImported) {
  const exportAll = async () => {
    try {
      const res = await api.get("/packages/export", { responseType: "blob" });
      const text = await res.data.text();
      downloadBlob(text, `packages-${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success("Export ready");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const importFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/packages/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const parts = [];
      if (data.imported) parts.push(`${data.imported} new`);
      if (data.updated) parts.push(`${data.updated} updated`);
      if (data.unchanged) parts.push(`${data.unchanged} unchanged`);
      const msg = parts.length ? `Import done · ${parts.join(" · ")}` : "Nothing changed";
      if (data.errors?.length) {
        toast.warning(`${msg}. First issue: ${data.errors[0]}`);
      } else {
        toast.success(msg);
      }
      onImported?.();
    } catch (err) {
      reportImportError(err);
    }
  };

  return { exportAll, importFile };
}

export default function PackageCsvIO({ onImported, isAdmin }) {
  const inputRef = useRef(null);
  const { exportAll, importFile } = usePackageCsv(onImported);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await importFile(file);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="flex items-center gap-2">
      <input
        ref={inputRef}
        type="file"
        accept=".csv"
        onChange={handleFileChange}
        className="hidden"
        data-testid="package-csv-input"
      />
      <button
        type="button"
        onClick={() => downloadBlob(TEMPLATE_HEADER, "packages-template.csv")}
        data-testid="package-csv-template"
        className="px-3 py-2 border border-canvas-line text-sm hover:border-ink flex items-center gap-1.5"
        title="Download a blank CSV with the correct headers"
      >
        <DownloadSimple size={13} weight="bold" />
        Template
      </button>
      {isAdmin && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          data-testid="package-csv-import"
          className="px-3 py-2 border border-canvas-line text-sm hover:border-ink flex items-center gap-1.5"
        >
          <UploadSimple size={13} weight="bold" />
          Import CSV
        </button>
      )}
      <button
        type="button"
        onClick={exportAll}
        data-testid="package-csv-export"
        className="px-3 py-2 border border-canvas-line text-sm hover:border-ink flex items-center gap-1.5"
      >
        <DownloadSimple size={13} weight="bold" />
        Export CSV
      </button>
    </div>
  );
}
